/** @jsxImportSource @emotion/react */
import { faPencil } from "@fortawesome/pro-regular-svg-icons";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
    Modal, ModalBody, ModalFooter, ModalHeader
} from "reactstrap";
import useConfig from "../../../../actions/Tenants/config/configHook";
import { layerQueryKey, useApplyLayerFieldToCache, usePatchLayerMutation } from "../../../../hooks/mutations/usePatchLayerMutation";
import { useRouteLayer } from "../../../../hooks/useCurrentEntities";
import MetaForm, { metaFormIsValid } from "../../../Forms/MetaForm";
import { Button } from "../../../Helper/Button";

export function EditLayerButton({ layer_type }) {
    const params = useParams();
    const [layerModalOpen, setLayerModal] = useState(params.action === "edit");

    useEffect(() => {
        if (params.action === "edit") {
            setLayerModal(true);
        }
    }, [params.action]);

    const { show_edit_button } = layer_type;

    if (!show_edit_button) return null;

    return (
        <>
            <Button
                key={0}
                onClick={() => setLayerModal(true)}
                color="secondary"
                outline
                icon={faPencil}
            >
                Edit
            </Button>

            <EditLayerModal
                key={1}
                isOpen={layerModalOpen}
                setLayerModal={setLayerModal}
            />
        </>
    );
}

interface EditLayerModalProps {
    setLayerModal: (boolean) => void;
    isOpen: boolean;
}

function EditLayerModal({ isOpen, setLayerModal }: EditLayerModalProps) {
    const config = useConfig();
    const layer = useRouteLayer();
    const patchMutation = usePatchLayerMutation();
    const applyLayerFieldToCache = useApplyLayerFieldToCache();
    const queryClient = useQueryClient();

    const layer_config = config.get_layer_config(layer);
    const meta_form = layer_config?.meta_form || [];

    // Snapshot the layer when the modal opens. Checksum validation skips a field
    // that still equals this copy, so a voyage label stays until it is edited.
    const baselineRef = useRef<{ open: boolean, layer: any }>({ open: false, layer: undefined });
    if (isOpen && layer && !baselineRef.current.open) {
        baselineRef.current = { open: true, layer };
    }
    if (!isOpen && baselineRef.current.open) {
        baselineRef.current = { open: false, layer: undefined };
    }
    const validationBaseline = baselineRef.current.layer;

    // Keystrokes only touch the cached layer; onDebounce owns the write.
    const setValue = (field, value) => applyLayerFieldToCache(layer, field, value);
    const onDebounce = (field, value) => {
        const field_config = meta_form.find((i) => i.name === field);
        if (field_config && metaFormIsValid([field_config], { [field]: value }, true, { baseline: validationBaseline })) {
            return patchMutation.mutate({ layer, field, value, keep_history: true });
        }
        return Promise.resolve();
    };

    const onSubmit = () => {
        if (meta_form && !metaFormIsValid(meta_form, layer, true, { baseline: validationBaseline })) return;
        // TODO: Add triggerLayerBusinessRules mutation when needed
        setLayerModal(false);
    };

    const onClose = () => {
        // Keystrokes were written to the cache only; refetch so a value onDebounce
        // rejected does not stay on the page as if it were saved.
        queryClient.invalidateQueries({ queryKey: layerQueryKey(layer) });
        queryClient.invalidateQueries({ queryKey: ["layers", layer?.tenant_id] });
        setLayerModal(false);
    };

    return (
        <Modal isOpen={isOpen} size="md">
            <ModalHeader toggle={onClose} onClick={onClose}>Edit {layer_config.text} {layer.label}</ModalHeader>

            <ModalBody>
                <MetaForm
                    meta={meta_form}
                    object={layer}
                    config={config}
                    setValue={setValue}
                    onDebounce={onDebounce}
                    validationBaseline={validationBaseline}
                />
            </ModalBody>

            <ModalFooter>
                <Button color="light" onClick={onClose}>Cancel</Button>
                <Button color="success" onClick={onSubmit}>Save</Button>
            </ModalFooter>
        </Modal>
    );
}
